import { useContext, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import gql from 'graphql-tag'
import { isEmpty } from 'lodash'
import Cookies from 'js-cookie'
import {
  Button,
  Input,
  Checkbox,
  WarnMesssage
} from '@collegebacker/backer-ui/ui'

import { SessionContext } from 'context/SessionProvider'
import { prepareRegistrationParams } from '../../../../cookie'
import { authenticatedUserFragment, graphQLErrorHandler, track } from 'utils'
import { setRegistrationAuthenticated } from 'utils/auth-utils'
import { Fund, User, Step } from '../../types'
import { CurrentFlow } from 'modules/contribution/types'
import { getQuery } from 'hooks'
import { emailValue } from 'modules/auth/LocalState'
import { validateEmail } from 'modules/auth/validation'
import { validateName } from 'modules/create-fund/components/CreateFundCollectName'
import {
  activationDateValue,
  amountValue,
  frequencyValue,
  firstDayValue,
  currentFlowValue
} from 'modules/contribution/LocalState'
import { buildPath } from 'routes'
import { isStarterGiftFlow } from 'modules/contribution/helpers'
import { startContributionUrl } from 'modules/contribution/helpers'
import { getFirstStepIfLoggedIn } from './GiftingModal'

import styles from './GiftingSignup.module.scss'

const CURRENT_STEP = 'signup'

const CREATE_BASIC_USER = gql`
  mutation CreateBasicUser($input: CreateBasicUserInputType!) {
    createBasicUser(input: $input) {
      webAuthToken
      user {
        ...userFragment
      }
    }
  }
  ${authenticatedUserFragment}
`

const GiftingSignup = ({
  dispatch,
  fund,
  updateCurrentUser,
  refetchCurrentUser,
  redirectToContributionFirstStepIfReload,
  user
}: {
  dispatch?: any
  fund: Fund
  updateCurrentUser: (attributes: object) => Promise<any>
  refetchCurrentUser?: () => Promise<any>
  redirectToContributionFirstStepIfReload?: () => void
  user: User
}) => {
  const history = useHistory()
  const { flow = currentFlowValue() }: { flow: CurrentFlow } = useParams()
  const context = useContext(SessionContext)

  user = user || {}
  const userExists = !!user.email
  let nextStep: Step = 'signup'

  const [firstName, setFirst] = useState(user.firstName || '')
  const [lastName, setLast] = useState(user.lastName || '')
  const [firstNameError, setFirstError] = useState('')
  const [lastNameError, setLastError] = useState('')
  const [email, setEmail] = useState(user.email || '')
  const [emailError, setEmailError] = useState('')
  const [globalError, setGlobalError] = useState<string | null>(null)
  const [createAccount, setCreateAccount] = useState(true)

  useEffect(() => {
    if (redirectToContributionFirstStepIfReload)
      redirectToContributionFirstStepIfReload()
  }, [])

  useEffect(() => {
    nextStep = getFirstStepIfLoggedIn({ user, flow })
  }, [user, flow])

  useEffect(() => {
    if (!!Cookies.get('authorization')) {
      dispatch &&
        dispatch({
          type: 'setActiveStep',
          payload: {
            activeStep: nextStep
          }
        })
    }
  }, [])

  function goToSignin(flow: CurrentFlow, password?: boolean) {
    const queryParams = getQuery()
    const redirectTo = isStarterGiftFlow(flow)
      ? startContributionUrl({
        flow: 'create-starter-gift',
        step: `${nextStep}`
      })
      : `${buildPath('giftingPage', {
        handle: fund.handle
      })}?modal=true&step=message`

    queryParams.append('redirectTo', redirectTo)

    queryParams.append(
      'giftInProgress',
      JSON.stringify({
        amount: amountValue(),
        firstDay: firstDayValue(),
        frequency: frequencyValue(),
        activationDate: activationDateValue()
      })
    )

    const pathname = password
      ? buildPath('loginPassword')
      : buildPath('loginEmail')
    history.push({
      pathname,
      search: queryParams.toString()
    })
  }

  const [createBasicUser, { loading }] = useMutation(CREATE_BASIC_USER, {
    onError: (error) => {
      if (error.message.includes('Email has already been taken')) {
        if (error.message.includes('Guest YES')) {
          dispatch &&
            dispatch({
              type: 'setActiveStep',
              payload: {
                activeStep: 'returning-guest',
                previousStep: 'signup'
              }
            })
        } else {
          goToSignin(flow, true)
        }
      } else {
        graphQLErrorHandler(setEmailError)(error)
      }
    },
    onCompleted: (data) => {
      setRegistrationAuthenticated({
        token: data.createBasicUser.webAuthToken,
        isGuest: !createAccount,
        done: async () => {
          context.logIn(data.createBasicUser.user)
          refetchCurrentUser && (await refetchCurrentUser())
          const activeStep = getFirstStepIfLoggedIn({ user: data.createBasicUser.user, flow })
          dispatch && dispatch({
            type: 'setActiveStep',
            payload: { activeStep }
          })
        }
      })
    }
  })

  return (
    <form
      className={styles.container}
      onSubmit={(event) => {
        event.preventDefault()

        const errors: any = validateName({ firstName, lastName })
        const emailError = validateEmail({ email })
        setFirstError(errors['firstName'])
        setLastError(errors['lastName'])
        setEmailError(emailError['email'] || '')

        if (isEmpty(emailError) && isEmpty(errors)) {
          if (userExists) {
            updateCurrentUser({
              variables: {
                input: {
                  firstName: firstName.trim(),
                  lastName: lastName.trim()
                }
              }
            })
              .then(() => {
                dispatch && dispatch({
                  type: 'setActiveStep',
                  payload: { activeStep: 'message', previousStep: 'signup' }
                })
              })
              .catch(graphQLErrorHandler(setGlobalError))
          } else {
            const input = prepareRegistrationParams({
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              email: email.trim(),
              guest: createAccount ? 'never' : 'current'
            })

            track('ContributionSignup', {
              step: CURRENT_STEP,
              email: input.email
            })

            createBasicUser({ variables: { input } })
          }
        }
      }}
    >
      <div className={styles.body}>
        <h2 className="typo-app-title-medium-adaptive">Tell us about you</h2>
        {!userExists && (
          <p className="typo-app-body-paragraph">
            Share your name and email so the lucky family will know who to thank.
          </p>
        )}
        <WarnMesssage text={globalError} type="error" />
        <div className={styles.inputsContainer}>
          <Input
            className={styles.input}
            name="firstName"
            label="First name"
            maxLength={20}
            value={firstName}
            onChange={(e: any) => {
              setFirst(e.target.value)
              setFirstError('')
            }}
            errorMessage={firstNameError}
            autoFocus={context.mobileAppFromV(1) ? false : true}
          />
          <Input
            className={styles.input}
            name="lastName"
            label="Last name"
            maxLength={20}
            value={lastName}
            onChange={(e: any) => {
              setLast(e.target.value)
              setLastError('')
            }}
            errorMessage={lastNameError}
          />
          <Input
            label="Your email"
            name="email"
            type="email"
            value={email}
            disabled={userExists}
            onChange={(event: any) => {
              setEmail(event.target.value)
              emailValue(event.target.value)
            }}
            errorMessage={emailError}
            className={styles.input}
          />
        </div>
        <div className={styles.ctaContainer}>
          {!userExists && (
            <Checkbox
              className={styles.checkboxContainer}
              name="createAccount"
              id="createAccount"
              checked={createAccount}
              onChange={(checked: boolean) => setCreateAccount(checked)}
            >
              <span className="typo-app-body-main">
                Create an account to manage your gift and receive family updates.
              </span>
            </Checkbox>
          )}
          <div className={styles.buttons}>
            <Button
              className={styles.submitCta}
              mode="outline"
              label="Continue"
              disabled={loading}
              type="submit"
            />
            {!userExists && (<Button
              mode="ghost"
              label="I already have an account"
              type="button"
              onClick={() => goToSignin(flow)}
            />)}
          </div>
        </div>
      </div>
    </form>
  )
}

export default GiftingSignup
